import { CountryCodeSelect } from "@components/shared/CountryCodeSelect";
import { DrivePickerButton } from "@components/shared/DrivePickerButton";
import { FileDropZone } from "@components/shared/FileDropZone";
import { NotesPanel } from "@components/shared/NotesPanel";
import { RefreshIndicator } from "@components/shared/RefreshIndicator";
import { SearchLocationSelect } from "@components/shared/SearchLocationSelect";
import { DELETE_CLIENT, UPDATE_CLIENT } from "@lib/graphql/mutations";
import { CLIENT_QUERY } from "@lib/graphql/queries";
import { useRecordChannel } from "@lib/hooks/useRecordChannel";
import { ArrowLeft, Pencil, Save, Trash2, Upload, X } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "urql";
import { DeleteClientDialog } from "./DeleteClientDialog";

export function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [{ data, fetching }, reexecute] = useQuery({ query: CLIENT_QUERY, variables: { id } });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useRecordChannel("Client", id!, refresh);
  const [, updateClient] = useMutation(UPDATE_CLIENT);
  const [, deleteClientMutation] = useMutation(DELETE_CLIENT);
  const [editing, setEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState<{
    campaigns: number;
    exclusiveCandidates: number;
    sharedCandidates: number;
    talentPools: number;
    clientContacts: number;
    notes: number;
    portalUsers: number;
  } | null>(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "", website: "", notes: "" });
  const [brandingForm, setBrandingForm] = useState({
    primary_color: "",
    secondary_color: "",
    accent_color: "",
  });
  const [brandingDirty, setBrandingDirty] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [preferencesForm, setPreferencesForm] = useState({
    likes: "",
    dislikes: "",
    right_to_work_policy: "",
    sponsorship_policy: "",
  });
  const [preferencesDirty, setPreferencesDirty] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingSearchLocation, setSavingSearchLocation] = useState(false);

  const client = data?.client;

  function startEditing() {
    if (!client) return;
    setForm({
      name: client.name || "",
      industry: client.industry || "",
      website: client.website || "",
      notes: client.notes || "",
    });
    setEditing(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const input: Record<string, string | undefined> = {
      id: id!,
      name: form.name,
      industry: form.industry || undefined,
      website: form.website || undefined,
      notes: form.notes || undefined,
    };
    const result = await updateClient({ input });
    if (result.data?.updateClient?.errors?.length === 0) {
      setEditing(false);
      reexecute({ requestPolicy: "network-only" });
    }
  }

  // Sync branding form when client data arrives
  const clientBranding = (client?.branding || {}) as Record<string, string>;
  const brandingInSync =
    brandingForm.primary_color === (clientBranding.primary_color || "") &&
    brandingForm.secondary_color === (clientBranding.secondary_color || "") &&
    brandingForm.accent_color === (clientBranding.accent_color || "");

  if (client && !brandingDirty && !brandingInSync) {
    setBrandingForm({
      primary_color: clientBranding.primary_color || "",
      secondary_color: clientBranding.secondary_color || "",
      accent_color: clientBranding.accent_color || "",
    });
  }

  // Sync preferences form when client data arrives
  const clientPreferences = (client?.preferences || {}) as Record<string, string>;
  const preferencesInSync =
    preferencesForm.likes === (clientPreferences.likes || "") &&
    preferencesForm.dislikes === (clientPreferences.dislikes || "") &&
    preferencesForm.right_to_work_policy === (clientPreferences.right_to_work_policy || "") &&
    preferencesForm.sponsorship_policy === (clientPreferences.sponsorship_policy || "");

  if (client && !preferencesDirty && !preferencesInSync) {
    setPreferencesForm({
      likes: clientPreferences.likes || "",
      dislikes: clientPreferences.dislikes || "",
      right_to_work_policy: clientPreferences.right_to_work_policy || "",
      sponsorship_policy: clientPreferences.sponsorship_policy || "",
    });
  }

  function handlePreferencesChange(field: string, value: string) {
    setPreferencesForm((prev) => ({ ...prev, [field]: value }));
    setPreferencesDirty(true);
  }

  async function handlePreferencesSave() {
    if (!client) return;
    setSavingPreferences(true);
    const result = await updateClient({
      input: { id: client.id, preferences: preferencesForm },
    });
    if (result.data?.updateClient?.errors?.length === 0) {
      setPreferencesDirty(false);
      reexecute({ requestPolicy: "network-only" });
    }
    setSavingPreferences(false);
  }

  function handleBrandingChange(field: string, value: string) {
    setBrandingForm((prev) => ({ ...prev, [field]: value }));
    setBrandingDirty(true);
  }

  async function handleBrandingSave() {
    if (!client) return;
    setSavingBranding(true);
    const result = await updateClient({
      input: { id: client.id, branding: brandingForm },
    });
    if (result.data?.updateClient?.errors?.length === 0) {
      setBrandingDirty(false);
      reexecute({ requestPolicy: "network-only" });
    }
    setSavingBranding(false);
  }

  async function handleLogoUpload(file: File) {
    if (!client) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const response = await fetch("/uploads", {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        body: formData,
      });
      const uploadData = await response.json();
      await attachLogoBySignedId(uploadData.signed_id);
    } finally {
      setUploadingLogo(false);
    }
  }

  async function attachLogoBySignedId(signedId: string) {
    if (!client) return;
    const result = await updateClient({
      input: { id: client.id, logoSignedId: signedId },
    });
    if (result.data?.updateClient?.errors?.length === 0) {
      reexecute({ requestPolicy: "network-only" });
    }
  }

  async function handleDeleteClick() {
    setLoadingImpact(true);
    setShowDeleteDialog(true);
    setDeleteImpact(null);
    const result = await deleteClientMutation({ input: { clientId: id } });
    setDeleteImpact(result.data?.deleteClient?.impact ?? null);
    setLoadingImpact(false);
  }

  async function handleDeleteConfirm() {
    setDeleting(true);
    const result = await deleteClientMutation({ input: { clientId: id, confirm: true } });
    if (result.data?.deleteClient?.enqueued) {
      navigate("/clients");
    }
    setDeleting(false);
  }

  if (fetching && !client) return <p className="text-sm text-text-muted">Loading...</p>;
  if (!client) return <p className="text-sm text-text-muted">Client not found</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/clients"
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-secondary"
        >
          <ArrowLeft size={16} /> Back to clients
        </Link>
        <RefreshIndicator active={fetching} />
      </div>

      <div className="flex items-start justify-between">
        {editing ? (
          <form onSubmit={handleSave} className="flex-1 space-y-3">
            <div>
              <label className="block text-xs font-medium text-text-muted">Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-muted">Industry</label>
                <input
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-muted">Website</label>
                <input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90"
              >
                <Save size={14} /> Save
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs font-medium text-text-muted hover:bg-card-hover"
              >
                <X size={14} /> Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-text-primary">{client.name}</h1>
              <div className="mt-1 flex gap-4 text-sm text-text-muted">
                {client.industry && <span>{client.industry}</span>}
                {client.website && <span>{client.website}</span>}
              </div>
            </div>
            <button
              type="button"
              onClick={startEditing}
              className="flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs font-medium text-text-muted hover:bg-card-hover"
            >
              <Pencil size={14} /> Edit
            </button>
          </>
        )}
      </div>

      {!editing && client.notes && (
        <div className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
          <h3 className="text-sm font-medium text-text-secondary">Notes</h3>
          <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{client.notes}</p>
        </div>
      )}

      {/* Notes */}
      <div className="mt-6">
        <NotesPanel
          notableType="Client"
          notableId={client.id}
          notes={client.attachedNotes ?? []}
          onChanged={() => reexecute({ requestPolicy: "network-only" })}
        />
      </div>

      {/* Branding */}
      <div className="mt-6">
        <h2 className="text-lg font-medium text-text-primary">Branding</h2>
        <p className="mt-1 text-sm text-text-muted">
          Client-specific branding for reports. Overrides organisation defaults.
        </p>
        <div className="mt-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
          <div className="flex items-center gap-6">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface">
              {client.logoUrl ? (
                <img
                  src={client.logoUrl}
                  alt="Client logo"
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-xs text-text-faint">No logo</span>
              )}
            </div>
            <FileDropZone
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              disabled={uploadingLogo}
              onFiles={(files) => handleLogoUpload(files[0])}
              overlayLabel="Drop logo here"
            >
              {({ openPicker }) => (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openPicker}
                    disabled={uploadingLogo}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-input-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Upload size={14} />
                    {uploadingLogo ? "Uploading..." : "Upload Logo"}
                  </button>
                  <DrivePickerButton
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    disabled={uploadingLogo}
                    onSignedId={(signedId) => attachLogoBySignedId(signedId)}
                  />
                </div>
              )}
            </FileDropZone>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(
              [
                ["primary_color", "Primary"],
                ["secondary_color", "Secondary"],
                ["accent_color", "Accent"],
              ] as const
            ).map(([field, label]) => (
              <div key={field}>
                <label className="block text-xs font-medium text-text-muted">{label}</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={brandingForm[field] || "#000000"}
                    onChange={(e) => handleBrandingChange(field, e.target.value)}
                    className="h-8 w-8 cursor-pointer rounded border border-input-border"
                  />
                  <input
                    type="text"
                    value={brandingForm[field]}
                    onChange={(e) => handleBrandingChange(field, e.target.value)}
                    placeholder="#000000"
                    className="block w-full rounded border border-border bg-surface px-2 py-1 text-sm"
                  />
                </div>
              </div>
            ))}
          </div>

          {brandingDirty && (
            <button
              onClick={handleBrandingSave}
              disabled={savingBranding}
              className="mt-3 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {savingBranding ? "Saving..." : "Save Branding"}
            </button>
          )}
        </div>
      </div>

      {/* Search Location */}
      <div className="mt-6">
        <h2 className="text-lg font-medium text-text-primary">Default Search Location</h2>
        <p className="mt-1 text-sm text-text-muted">
          Overrides the organisation default for this client's campaigns.
        </p>
        <div className="mt-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
          <CountryCodeSelect
            value={client.searchCountryCodes ?? []}
            onChange={async (codes) => {
              setSavingSearchLocation(true);
              const result = await updateClient({
                input: { id: client.id, searchCountryCodes: codes },
              });
              if (result.data?.updateClient?.errors?.length === 0) {
                reexecute({ requestPolicy: "network-only" });
              }
              setSavingSearchLocation(false);
            }}
          />
          <div className="mt-3">
            <SearchLocationSelect
              value={client.searchLocations ?? []}
              onChange={async (locations) => {
                setSavingSearchLocation(true);
                const result = await updateClient({
                  input: { id: client.id, searchLocations: locations },
                });
                if (result.data?.updateClient?.errors?.length === 0) {
                  reexecute({ requestPolicy: "network-only" });
                }
                setSavingSearchLocation(false);
              }}
              label="Narrower Locations"
              hint="Cities or regions within those countries, if searches aren't country-wide."
            />
          </div>
          {savingSearchLocation && <p className="mt-1 text-xs text-text-muted">Saving...</p>}
        </div>
      </div>

      {/* Preferences */}
      <div className="mt-6">
        <h2 className="text-lg font-medium text-text-primary">Preferences</h2>
        <p className="mt-1 text-sm text-text-muted">
          Client-level preferences surfaced to the AI assistant when working on campaigns.
        </p>
        <div className="mt-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(
              [
                [
                  "likes",
                  "Likes",
                  "What the client values (e.g. Big 4 background, international experience)",
                ],
                ["dislikes", "Dislikes", "What the client avoids (e.g. job hoppers, competitors)"],
                ["right_to_work_policy", "Right to Work", "Right to work requirements"],
                ["sponsorship_policy", "Sponsorship", "Willingness to sponsor visas"],
              ] as const
            ).map(([field, label, placeholder]) => (
              <div key={field}>
                <label className="block text-xs font-medium text-text-muted">{label}</label>
                <textarea
                  value={preferencesForm[field]}
                  onChange={(e) => handlePreferencesChange(field, e.target.value)}
                  placeholder={placeholder}
                  rows={2}
                  className="mt-1 w-full rounded border border-border bg-surface px-2 py-1 text-sm"
                />
              </div>
            ))}
          </div>

          {preferencesDirty && (
            <button
              onClick={handlePreferencesSave}
              disabled={savingPreferences}
              className="mt-3 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {savingPreferences ? "Saving..." : "Save Preferences"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-medium text-text-primary">Contacts</h2>
        {client.contacts.length === 0 ? (
          <p className="mt-2 text-sm text-text-muted">No contacts yet</p>
        ) : (
          <div className="mt-2 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-card-alt">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Phone
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {client.contacts.map(
                  (contact: {
                    id: string;
                    name: string;
                    title?: string;
                    email?: string;
                    phone?: string;
                    isPrimary: boolean;
                  }) => (
                    <tr key={contact.id}>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {contact.name}
                        {contact.isPrimary && (
                          <span className="ml-2 rounded bg-accent-light px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-400">
                            Primary
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {contact.title || "\u2014"}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {contact.email || "\u2014"}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {contact.phone || "\u2014"}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-medium text-text-primary">Campaigns</h2>
        {client.campaigns?.length === 0 ? (
          <p className="mt-2 text-sm text-text-muted">No campaigns yet</p>
        ) : (
          <div className="mt-2 space-y-2">
            {client.campaigns?.map((campaign: { id: string; title: string; status: string }) => (
              <Link
                key={campaign.id}
                to={`/campaigns/${campaign.id}`}
                className="block rounded-lg bg-card p-3 shadow-sm ring-1 ring-ring transition-colors hover:bg-card-hover"
              >
                <span className="font-medium text-accent">{campaign.title}</span>
                <span className="ml-2 text-sm text-text-muted">{campaign.status}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {client.canDelete && (
        <div className="mt-10 rounded-lg border border-error/30 bg-error/5 p-4">
          <h2 className="text-sm font-semibold text-error">Delete client</h2>
          <p className="mt-1 text-xs text-text-muted">
            Permanently delete this client and all its campaigns. This cannot be undone.
          </p>
          <button
            type="button"
            onClick={handleDeleteClick}
            className="mt-3 flex items-center gap-1 rounded-md bg-error px-3 py-1.5 text-xs font-medium text-white hover:bg-error/90"
          >
            <Trash2 size={14} /> Delete client
          </button>
        </div>
      )}

      <DeleteClientDialog
        open={showDeleteDialog}
        clientName={client.name}
        impact={deleteImpact}
        loading={loadingImpact}
        deleting={deleting}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
